import { VocalIsolationSettings } from "@/data/convert-settings.data";
import { ConversionEffects, EffectTab } from "@/data/convert-effects.data";
import { ConvertBaseStorage } from "@/services/storage/base/convert-base-storage";
import { BasePendingConfig } from "@/services/storage/storage-mixins/pending-config-storage-mixin";
import {
  DatasetDurationManager,
  DatasetSizeManager,
} from "@/services/license/base/license-manager-abstractions";
import {
  ConvertBaseSettingsField,
  ConvertBaseSettingsStorageMixin,
} from "@/services/storage/storage-mixins/settings-mixins/convert-base-settings-storage-mixin";

export interface ConvertBaseSettings {
  vocalIsolationSettings: VocalIsolationSettings;
  conversionEffects: ConversionEffects;
}

export interface ConvertBaseContextState<TSettings> {
  settings: TSettings;
  effectTabs: EffectTab[];
  enabledTabs: EffectTab[];
  selectedEffectTab?: EffectTab;
  setRemoveReverbEcho: (value: boolean) => void;
  isTabSelected: (tab: EffectTab) => boolean;
  isTabEnabled: (tab: EffectTab) => boolean;
  handleTabSelection: (tab: EffectTab) => void;
  handleEffectTabSwitcherClick: (tab: EffectTab) => void;
  handleEffectParameterValueChanged: (
    tabId: string,
    parameterId: string,
    value: number,
  ) => void;
  handlePitchChange: (value: number) => void;
  handleResetAllEffectTabsClick: () => void;
  handleResetEffectTabParametersClick: (tab: EffectTab) => void;
  setSelectedEffectTabId: (tabId: string) => void;
  licenseManager: DatasetSizeManager & DatasetDurationManager;
}

export const useConvertBaseContextProvider = <
  TSettings extends ConvertBaseSettings,
  TConfig extends BasePendingConfig,
  TSettingsField extends ConvertBaseSettingsField,
  TSettingsStorage extends ConvertBaseSettingsStorageMixin<TSettingsField>,
>(
  setSettings: (
    value: ((prevState: TSettings) => TSettings) | TSettings,
  ) => void,
  sessionStorage: ConvertBaseStorage<
    TSettings,
    TConfig,
    TSettingsField,
    TSettingsStorage
  >,
) => {
  const handlePitchChange = async (value: number) => {
    await sessionStorage.settingStorage.setSettings(
      value,
      "pitch" as TSettingsField,
    );
    setSettings((prevState) => ({
      ...prevState,
      conversionEffects: {
        ...prevState.conversionEffects,
        pitchChangeLevel: value,
      },
    }));
  };

  const setRemoveInstrumentals = async (value: boolean) => {
    await sessionStorage.settingStorage.setSettings(
      value,
      "removeInstrumentals" as TSettingsField,
    );
    setSettings((prevState) => ({
      ...prevState,
      vocalIsolationSettings: {
        ...prevState.vocalIsolationSettings,
        separateInstruments: value,
      },
    }));
  };

  const setRemoveReverbEcho = async (value: boolean) => {
    await sessionStorage.settingStorage.setSettings(
      value,
      "removeReverb" as TSettingsField,
    );
    setSettings((prevState) => ({
      ...prevState,
      vocalIsolationSettings: {
        ...prevState.vocalIsolationSettings,
        removeReverbAndEcho: value,
      },
    }));
  };

  return {
    handlePitchChange,
    setRemoveInstrumentals,
    setRemoveReverbEcho,
  };
};

export const startConvertBaseConfigCreator = <
  TConfig extends ConvertBaseSettings,
>(
  formData: FormData,
  enabledTabs: EffectTab[],
  config: TConfig,
) => {
  formData.append(
    "separateInstrumentals",
    config.vocalIsolationSettings.separateInstruments.toString(),
  );

  formData.append(
    "removeReverb",
    config.vocalIsolationSettings.removeReverbAndEcho.toString(),
  );

  enabledTabs.forEach((tab) => {
    tab.getParameters().forEach((parameter) => {
      formData.append(parameter.id, parameter.value.toString());
    });
  });
};
